import * as React from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Grid, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';
import { useAssignContactStatus } from '../../../../sic-lib/api/hooks/useContact';
import { useUserContactStatuses } from '../../../../sic-lib/api/hooks/useUserContactStatuses';
import { Contact } from '../../../../sic-lib/types/Contact';
import { hasMatchingStatusId } from '../../../../sic-lib/utils/functionalFilters';
import MenuSetContactStatusRaw from './MenuSetContactStatusRaw/MenuSetContactStatusRaw';

const useStyles = makeStyles()((theme: Theme) => ({
    btnStatus: {
        marginLeft: theme.spacing(1),
        color: theme.palette.btnDefault.main,
        '&:hover': {
            backgroundColor: theme.palette.btnDefault.light,
        },
    },
}));

interface Props {
    contact: Contact;
}

export default function MenuSetContactStatus({ contact }: Props) {
    const { classes } = useStyles();

    const { mutate: assignContactStatus } = useAssignContactStatus(contact.contactId);

    const { data: userStatuses } = useUserContactStatuses();

    const statusName = React.useMemo(() => {
        return contact.statusId !== null
            ? userStatuses?.filter(hasMatchingStatusId(contact.statusId))[0]?.status || 'Set Status'
            : 'Set Status';
    }, [userStatuses, contact.statusId]);

    const handleChange = async (event: React.MouseEvent<HTMLElement>) => {
        handleClose();

        const { statusId } = event.currentTarget.dataset;

        if (statusId === undefined) return;

        assignContactStatus(+statusId);
    };

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Grid container flexDirection="row" alignItems="center" justifyContent="center">
            <Typography variant="caption">Contact Status:</Typography>
            <Tooltip title="Edit Contact Status" placement="top" disableInteractive>
                <Button
                    className={classes.btnStatus}
                    aria-haspopup="true"
                    aria-controls={`menu_contact_status_${contact.contactId}`}
                    aria-expanded={Boolean(anchorEl)}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleOpen}
                >
                    {statusName}
                </Button>
            </Tooltip>
            {Boolean(anchorEl) && (
                <MenuSetContactStatusRaw
                    menuId={`menu_contact_status_${contact.contactId}`}
                    anchorEl={anchorEl}
                    contactId={contact.contactId}
                    statusId={contact.statusId ? contact.statusId : null}
                    statusList={userStatuses || []}
                    onChangeHandler={handleChange}
                    onCloseHandler={handleClose}
                />
            )}
        </Grid>
    );
}
